import { DatePipe } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LaddaModule } from 'angular2-ladda';
import { TooltipModule } from 'ngx-bootstrap';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ANIMATION_TYPES, LoadingModule } from 'ngx-loading';

import { ContractDetailComponent } from 'app/admin/contracts/admin-contract-detail.component';
import { AdminContractsComponent } from 'app/admin/contracts/admin-contracts.component';
import { ExportConfigurationsComponent } from 'app/admin/export/configurations/export-configurations.component';
import { FcdtoNcisComponent } from 'app/admin/export/fcdtoncis/fcdtoncis.component';
import { ExportMstComponent } from 'app/admin/export/mst/export-mst.component';
import { ExportOthersComponent } from 'app/admin/export/others/export-others.component';
import { ImportCsvComponent } from 'app/admin/import/csv/import-csv.component';
import { ImportShapesComponent } from 'app/admin/import/shapes/import-shapes.component';
import { AdminLocationsOverviewComponent } from 'app/admin/locations/admin-locations-overview.component';
import { MaintenanceComponent } from 'app/admin/maintenance/maintenance.component';
import { OsmVersionComponent } from 'app/admin/osm-version/osm-version.component';
import { TaskComponent } from 'app/admin/tasks/task.component';
import { AdminUserDetailComponent } from 'app/admin/users/admin-user-detail.component';
import { AdminUsersComponent } from 'app/admin/users/admin-users.component';
import { AppComponent } from 'app/app.component';
import { appRoutes } from 'app/app.routing';
import { ContractsComponent } from 'app/contracts/contracts.component';
import { FutureDateValidator } from 'app/directives';
import { ErrorsHandler } from 'app/errors-handler';
import { AuthGuard } from 'app/guards/auth.guard';
import { RoleApplicationAdminGuard } from 'app/guards/role-application-admin.guard';
import { RoleLocationAdminGuard } from 'app/guards/role-location-admin.guard';
import { RoleLocationOrMapAdminGuard } from 'app/guards/role-location-or-map-admin.guard';
import { RoleMapAdminGuard } from 'app/guards/role-map-admin.guard';
import { DialogChangeOriginalLinearComponent } from 'app/locations/details/linear/change-original-linear/dialog-change-original-linear.component';
import { LinearDetailsComponent } from 'app/locations/details/linear/linear-details.component';
import { PointDetailsComponent } from 'app/locations/details/point/point-details.component';
import { LinearInsertComponent } from 'app/locations/insert/linear/linear-insert.component';
import { PointInsertComponent } from 'app/locations/insert/point/point-insert.component';
import { LocationsComponent } from 'app/locations/locations.component';
import { LoginKeycloakCallbackComponent } from 'app/login-keycloak-callback/login-keycloak-callback.component';
import { LoginKeycloakComponent } from 'app/login-keycloak/login-keycloak.component';
import { LogoutKeycloakComponent } from 'app/logout-keycloak/logout-keycloak.component';
import { PageNotFoundComponent } from 'app/pagenotfound.component';
import { OauthRepository } from 'app/repositories';
import {
  AuthorizationService,
  ChangelogService,
  ContractService,
  ContractSettingsService,
  CsvService,
  DownloadService,
  ErrorService,
  FcdProviderService,
  GeojsonService,
  HttpService,
  MaintenanceService,
  MappingTablesService,
  MapService,
  MstService,
  MstToNcisService,
  NwbVersionService,
  OAuthService,
  OsmVersionService,
  ReportService,
  ServiceDeskService,
  ShapefileService,
  SiterecordCommentService,
  SiterecordService,
  StorageService,
  TaskService,
  UserService,
  ValidationService,
  VildChangesService,
  VildVersionService
} from 'app/services';
import { SharedModule } from 'app/shared/shared.module';
import { DateUtil } from 'app/utils';
import { VildVersionComponent } from 'app/vildversion/vild-version.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AdminContractsComponent,
    AdminLocationsOverviewComponent,
    AdminUserDetailComponent,
    AdminUsersComponent,
    AppComponent,
    ContractDetailComponent,
    ContractsComponent,
    DialogChangeOriginalLinearComponent,
    ExportConfigurationsComponent,
    ExportMstComponent,
    ExportOthersComponent,
    FcdtoNcisComponent,
    FutureDateValidator,
    ImportCsvComponent,
    ImportShapesComponent,
    LinearDetailsComponent,
    LinearInsertComponent,
    LocationsComponent,
    LoginKeycloakCallbackComponent,
    LoginKeycloakComponent,
    LogoutKeycloakComponent,
    MaintenanceComponent,
    OsmVersionComponent,
    PageNotFoundComponent,
    PointDetailsComponent,
    PointInsertComponent,
    TaskComponent,
    VildVersionComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    HttpClientModule,
    HttpModule,
    LaddaModule.forRoot({
      style: 'zoom-in',
      spinnerColor: 'white',
      spinnerLines: 12
    }),
    LoadingModule.forRoot({
      animationType: ANIMATION_TYPES.circleSwish,
      backdropBackgroundColour: 'rgba(0,0,0,0.3)',
      backdropBorderRadius: '4px',
      primaryColour: '#ffffff',
      secondaryColour: '#ffffff',
      tertiaryColour: '#ffffff'
    }),
    ModalModule.forRoot(),
    RouterModule.forRoot(appRoutes),
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    TooltipModule
  ],
  providers: [
    AuthGuard,
    AuthorizationService,
    ChangelogService,
    ContractService,
    ContractSettingsService,
    CsvService,
    DatePipe,
    DateUtil,
    DownloadService,
    ErrorService,
    FcdProviderService,
    GeojsonService,
    HttpService,
    MaintenanceService,
    MappingTablesService,
    MapService,
    MstService,
    MstToNcisService,
    NwbVersionService,
    OauthRepository,
    OAuthService,
    OsmVersionService,
    ReportService,
    RoleApplicationAdminGuard,
    RoleLocationAdminGuard,
    RoleLocationOrMapAdminGuard,
    RoleMapAdminGuard,
    ServiceDeskService,
    ShapefileService,
    SiterecordCommentService,
    SiterecordService,
    StorageService,
    TaskService,
    UserService,
    ValidationService,
    VildChangesService,
    VildVersionService,
    {
      provide: ErrorHandler,
      useClass: ErrorsHandler
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts